"use client";

import { ArrowRight, BadgeCheck, Headphones, IdCard } from "@/components/icons";
import {
  BadgesSection,
  ProductAnalyticsSection,
  ProductSessionBuilderSection,
  TrustedBySection,
} from "@/components/sections";
import AtAGlance from "@/components/sections/products/shared/at-a-glance";
import ProductHeroRibbon from "@/components/sections/products/shared/hero-ribbon";
import FaqSection from "@/components/sections/products/shared/faq";
import ProxyComparisonDe from "@/components/sections/de/shared/proxy-comparison";
import CompareAlternativesDe from "@/components/sections/de/shared/compare-alternatives";
import ReviewsDe from "@/components/sections/de/shared/reviews";
import PricingCtaDe from "@/components/sections/de/shared/pricing-cta";
import { LanguageSwitchLink } from "@/components/ui";
import { FREE_TRIAL_URL, ROUTES } from "@/lib/constants";
import MobilePricingFeaturesDe from "./parts/pricing-features";
import MobileProductHighlightsDe from "./parts/product-highlights";
import MobileForDevelopersDe from "./parts/for-developers";
import MobileUseCasesDe from "./parts/use-cases";
import MobilePricingCardsDe from "./parts/pricing-cards";
import { MOBILE_PRODUCT_FAQS_DE } from "./parts/faq-data";

/* Exact structural clone of /proxy-server/mobile-proxies, translated section by
   section — same order, same CTAs. Two deliberate exceptions, left in English:
   ProductAnalyticsSection and ProductSessionBuilderSection are interactive
   mock-ups of the (English-only) real dashboard UI; translating the demo
   would misrepresent what the actual product looks like, same principle as
   not claiming German-language support. */

const heroBenefits = [
  { label: "KYC erforderlich", icon: IdCard },
  { label: "30 Tage Geld-zurück-Garantie", icon: BadgeCheck },
  { label: "Premium-Support", icon: Headphones },
];

function Band({ children }: { children: React.ReactNode }) {
  return <div className="full-bleed band-wash bg-band">{children}</div>;
}

export default function MobileProxiesDePage() {
  return (
    <main id="main-content" tabIndex={-1} className="relative">
      <LanguageSwitchLink
        href={ROUTES.productMobileProxies}
        hrefLang="en"
        label="English version"
      />

      <ProductHeroRibbon
        headingId="mobile-product-hero-heading"
        headingLine1="Die besten 4G- und 5G-Mobile-Proxies."
        headingLine2="Pay per GB, skalieren Sie nach Bedarf."
        description="Premium mobile IPs weltweit. Targeting nach Stadt, Rotation auf Abruf und Abrechnung nach Gigabyte."
        benefits={heroBenefits}
        ctas={[
          {
            label: "Kostenlos registrieren",
            href: FREE_TRIAL_URL,
            trailingIcon: <ArrowRight size={16} aria-hidden="true" />,
          },
          { label: "Mit Residential Proxies vergleichen", href: "#compare-proxies" },
        ]}
      />

      <AtAGlance
        headingId="mobile-at-a-glance-heading"
        heading="Proxidize Mobile Proxies auf einen Blick"
        intro="Proxidize bietet verwaltete 4G/5G-Mobile-Proxies mit Premium-Mobile-IPs, rotierenden und sticky Sessions, Städte- und ASN/ISP-Targeting sowie HTTP(S)/SOCKS5-Unterstützung. Pläne starten bei $2/GB, ohne Begrenzung der Access Points und mit vollem API-Zugriff. KYC ist erforderlich."
        rows={[
          { label: "Startpreis", value: "$2/GB, einheitlich über alle Pläne" },
          { label: "Netzwerk", value: "4G/5G-Mobile-IPs" },
          { label: "Targeting", value: "Land, Stadt, ASN/ISP" },
          { label: "Sessions", value: "Rotierend oder sticky, bis zu 3 Tage" },
          { label: "Protokolle", value: "HTTP, HTTPS, SOCKS5" },
          { label: "Authentifizierung", value: "Benutzername/Passwort oder IP-Whitelist" },
          { label: "Access Points", value: "Keine Begrenzung" },
          { label: "KYC", value: "Erforderlich" },
        ]}
      />

      <div className="sm:hidden">
        <TrustedBySection />
      </div>

      <MobilePricingFeaturesDe />
      <MobileProductHighlightsDe />

      <ProductAnalyticsSection variant="mobile" />
      <Band>
        <ProductSessionBuilderSection variant="mobile" />
      </Band>

      <MobileForDevelopersDe />
      <MobileUseCasesDe />
      <ProxyComparisonDe variant="mobile" />
      <MobilePricingCardsDe />

      <ReviewsDe excludeProduct="residential" />
      <BadgesSection />

      <FaqSection
        faqs={MOBILE_PRODUCT_FAQS_DE}
        heading="Häufig gestellte Fragen"
        description="Antworten zu Preisen, Targeting, Geschwindigkeit und Verifizierung für Mobile Proxies."
        headingId="de-mobile-faq"
        contactLabel="Unser Team kontaktieren"
      />

      <CompareAlternativesDe />
      <PricingCtaDe id="cta-heading" heading="Bereit für echte mobile IPs?" />
    </main>
  );
}
